"use client";

import { useEffect, useRef } from "react";



const termsItems = [
  {
    title: "Service Definition",
    content:
      "Scaled-Out Limited provides advertising infrastructure services exclusively. We operate Meta ad account systems. We do not run campaigns, provide creative services, or guarantee advertising performance. Your use of our infrastructure is governed by these terms.",
  },
  {
    title: "Account Eligibility",
    content:
      "Access is granted following infrastructure review. Minimum monthly spend thresholds apply. We reserve the right to decline or terminate service based on operational capacity, compliance risk, or policy violation. All applicants must provide accurate business information.",
  },
  {
    title: "Payment Terms",
    content:
      "Fees are calculated based on capital deployment volume. Payment is due upon invoice issuance. Late payments may result in service suspension. All fees are non-refundable. Currency conversions are processed at prevailing market rates.",
  },
  {
    title: "Service Limitations",
    content:
      "We maintain 99%+ uptime targets but do not guarantee uninterrupted service. Maintenance windows are scheduled with advance notice. Force majeure events, platform policy changes, or regulatory actions may impact service availability without liability.",
  },
  {
    title: "Prohibited Activities",
    content:
      "Users may not deploy capital for illegal products, deceptive practices, or policy-violating campaigns. Attempts to circumvent platform restrictions, engage in fraudulent activity, or misrepresent business operations will result in immediate termination.",
  },
  {
    title: "Liability & Indemnification",
    content:
      "Scaled-Out Limited is not liable for advertising performance, account suspensions, or capital loss. Users indemnify us against claims arising from their campaign content, targeting practices, or regulatory violations. Maximum liability is limited to fees paid in the preceding 30 days.",
  },
  {
    title: "Termination Rights",
    content:
      "Either party may terminate with 14 days written notice. We reserve the right to immediate termination for policy violations, payment default, or operational risk. Upon termination, all outstanding fees become immediately due. No refunds are provided for prepaid services.",
  },
  {
    title: "Modifications",
    content:
      "These terms may be updated with 30 days notice. Continued use of services constitutes acceptance of modified terms. Material changes will be communicated via email to registered account contacts.",
  },
];

const privacyItems = [
  {
    title: "Data Collection",
    content:
      "We collect business information (company name, contact details, spend data), technical data (IP addresses, device information, usage patterns), and financial data (payment methods, transaction history). Collection occurs during onboarding, service usage, and payment processing.",
  },
  {
    title: "Data Usage",
    content:
      "Your data is used to operate infrastructure services, process payments, prevent fraud, comply with legal obligations, and improve service quality. We do not sell personal data to third parties. Data may be shared with payment processors, compliance partners, and platform providers as operationally necessary.",
  },
  {
    title: "Data Storage",
    content:
      "Data is stored on secure servers in the European Economic Area and United Kingdom. We employ encryption, access controls, and regular security audits. Retention periods align with legal requirements and operational necessity—typically 7 years for financial records.",
  },
  {
    title: "Third-Party Services",
    content:
      "We integrate with Meta Business Suite, payment processors, and compliance verification services. These providers operate under their own privacy policies. We conduct due diligence on all third-party processors to ensure adequate data protection standards.",
  },
  {
    title: "User Rights",
    content:
      "You have the right to access your data, request corrections, demand deletion (subject to legal retention requirements), restrict processing, and data portability. Requests are processed within 30 days. Contact privacy@scaled-out.com to exercise these rights.",
  },
  {
    title: "Cookies & Tracking",
    content:
      "We use essential cookies for authentication and session management. Analytics cookies track usage patterns to improve service quality. You may disable non-essential cookies via browser settings, though this may impact functionality.",
  },
  {
    title: "Data Breaches",
    content:
      "In the event of a data breach affecting personal information, we will notify affected users within 72 hours and report to relevant authorities as required by GDPR and UK data protection laws. Notification includes breach nature, affected data, and remediation steps.",
  },
  {
    title: "International Transfers",
    content:
      "Data may be transferred to jurisdictions outside the EEA/UK where operationally necessary. All transfers comply with GDPR requirements via Standard Contractual Clauses or adequacy decisions. We ensure equivalent protection standards across all jurisdictions.",
  },
];

const complianceItems = [
  {
    title: "Regulatory Framework",
    content:
      "Scaled-Out Limited operates under UK Companies House registration. We comply with GDPR, UK Data Protection Act 2018, Electronic Commerce Regulations 2002, and applicable advertising standards. Our infrastructure adheres to Meta Business Terms and Platform Policies.",
  },
  {
    title: "Financial Compliance",
    content:
      "We maintain anti-money laundering (AML) and know-your-customer (KYC) procedures aligned with UK Financial Conduct Authority guidelines. Large transactions are monitored. Suspicious activity is reported to relevant authorities. We do not facilitate transactions for sanctioned entities.",
  },
  {
    title: "Platform Policy Adherence",
    content:
      "All infrastructure operations comply with Meta Advertising Policies, Community Standards, and Business Terms. We monitor policy updates and adjust systems accordingly. Users are responsible for ensuring their campaigns meet platform requirements.",
  },
  {
    title: "Tax Compliance",
    content:
      "We are VAT-registered in the United Kingdom. Invoices include applicable VAT charges. International clients may be subject to reverse charge mechanisms. We provide documentation for tax reporting purposes upon request.",
  },
  {
    title: "Audit & Transparency",
    content:
      "We maintain detailed transaction records, access logs, and operational documentation. Annual financial audits are conducted by independent accountants. Compliance reports are available to regulatory authorities upon lawful request.",
  },
  {
    title: "Dispute Resolution",
    content:
      "Disputes are governed by English law and subject to the exclusive jurisdiction of English courts. We encourage informal resolution before legal proceedings. Mediation services are available for commercial disputes.",
  },
];

export default function LegalContent() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref?.current?.querySelectorAll(".reveal")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <div ref={ref}>
      {/* Hero */}
      <section className="relative pt-40 pb-24 bg-[#0B0F14] overflow-hidden">
        <div className="animated-grid absolute inset-0 z-0 opacity-30" />
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(0,230,118,0.06) 0%, transparent 70%)",
          }}
        />
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="tag-badge mx-auto mb-6 inline-flex reveal">Legal</div>
          <h1 className="hero-title text-[#F0F4F8] mb-6 reveal reveal-delay-1">
            Terms, Privacy &{" "}
            <span className="text-[#00E676]">Compliance.</span>
          </h1>
          <p className="text-[#6B7A8D] text-lg max-w-2xl mx-auto leading-relaxed reveal reveal-delay-2">
            Transparent operational framework. No hidden clauses. No ambiguous
            language. Infrastructure services governed by clear, enforceable
            terms.
          </p>
        </div>
      </section>
      {/* Terms of Service */}
      <section className="py-24 bg-[#11161D]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-16 reveal">
            <div className="tag-badge mb-6 inline-flex">Section 01</div>
            <h2 className="section-title text-[#F0F4F8] mb-4">
              Terms of <span className="text-[#00E676]">Service</span>
            </h2>
            <p className="text-[#6B7A8D] text-base leading-relaxed max-w-3xl">
              Binding agreement governing infrastructure access, payment
              obligations, and operational responsibilities. Read carefully
              before deployment.
            </p>
          </div>

          <div className="space-y-4 reveal reveal-delay-1">
            <>
              {termsItems?.map((item, i) => (
                <div key={i} className="card-neon rounded-2xl p-8">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-8 h-8 rounded-full border border-[rgba(0,230,118,0.3)] flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span className="text-[#00E676] font-mono text-xs font-bold">
                        {String(i + 1)?.padStart(2, "0")}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-[#F0F4F8] font-bold text-base mb-3">
                        {item?.title}
                      </h3>
                      <p className="text-[#6B7A8D] text-sm leading-relaxed">
                        {item?.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </>
          </div>
        </div>
      </section>
      {/* Privacy Policy */}
      <section className="py-24 bg-[#0B0F14]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-16 reveal">
            <div className="tag-badge mb-6 inline-flex">Section 02</div>
            <h2 className="section-title text-[#F0F4F8] mb-4">
              Privacy <span className="text-[#00E676]">Policy</span>
            </h2>
            <p className="text-[#6B7A8D] text-base leading-relaxed max-w-3xl">
              Comprehensive data handling practices. GDPR-compliant collection,
              storage, and processing protocols. Your data rights explained.
            </p>
          </div>

          <div className="space-y-4 reveal reveal-delay-1">
            <>
              {privacyItems?.map((item, i) => (
                <div key={i} className="card-neon rounded-2xl p-8">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-8 h-8 rounded-full border border-[rgba(0,230,118,0.3)] flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span className="text-[#00E676] font-mono text-xs font-bold">
                        {String(i + 1)?.padStart(2, "0")}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-[#F0F4F8] font-bold text-base mb-3">
                        {item?.title}
                      </h3>
                      <p className="text-[#6B7A8D] text-sm leading-relaxed">
                        {item?.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </>
          </div>
        </div>
      </section>
      {/* Compliance */}
      <section className="py-24 bg-[#11161D]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-16 reveal">
            <div className="tag-badge mb-6 inline-flex">Section 03</div>
            <h2 className="section-title text-[#F0F4F8] mb-4">
              Compliance <span className="text-[#00E676]">Framework</span>
            </h2>
            <p className="text-[#6B7A8D] text-base leading-relaxed max-w-3xl">
              Regulatory adherence across jurisdictions. Financial compliance,
              platform policy alignment, and audit transparency.
            </p>
          </div>

          <div className="space-y-4 reveal reveal-delay-1">
            <>
              {complianceItems?.map((item, i) => (
                <div key={i} className="card-neon rounded-2xl p-8">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-8 h-8 rounded-full border border-[rgba(0,230,118,0.3)] flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span className="text-[#00E676] font-mono text-xs font-bold">
                        {String(i + 1)?.padStart(2, "0")}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-[#F0F4F8] font-bold text-base mb-3">
                        {item?.title}
                      </h3>
                      <p className="text-[#6B7A8D] text-sm leading-relaxed">
                        {item?.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </>
          </div>
        </div>
      </section>
      {/* Contact Strip */}
      <section className="py-16 bg-[#0B0F14]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="card-neon rounded-2xl p-10 text-center reveal">
            <h3 className="text-[#F0F4F8] font-bold text-xl mb-3">
              Legal Inquiries?
            </h3>
            <p className="text-[#6B7A8D] text-sm leading-relaxed mb-6 max-w-2xl mx-auto">
              For questions regarding these terms, privacy practices, or
              compliance matters, contact our legal team directly.
            </p>
            <div className="flex items-center justify-center gap-2">
              <span
                className="w-2 h-2 rounded-full bg-[#00E676]"
                style={{ boxShadow: "0 0 8px rgba(0,230,118,0.8)" }}
              />
              <a
                href="mailto:legal@scaled-out.com"
                className="text-[#00E676] font-mono text-sm hover:underline"
              >
                legal@scaled-out.com
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}